import React, { useMemo } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  ChartData,
  ChartOptions,
  TooltipItem,
} from 'chart.js';

import {
  AbcCurveChartRow,
  AtRiskChartRow,
  AverageTicketChartRow,
  BestCustomersChartRow,
  CustomerIntelligenceChartResponse,
  CustomerReportType,
  PurchaseFrequencyChartRow,
  SalesByNeighborhoodChartRow,
} from '../../types';
import {
  ChartCanvas,
  ChartContext,
  ChartNotice,
  ChartPlot,
  ChartSection,
  ChartStatus,
  ChartViewport,
  RetryChartButton,
  TextAlternative,
} from './styles';

interface CustomerIntelligenceChartProps {
  reportType: CustomerReportType;
  response: CustomerIntelligenceChartResponse | null;
  totalResults?: number;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const RANKING_LIMIT = 10;
const HORIZONTAL_MAX_BAR_THICKNESS = 28;
const VERTICAL_MAX_BAR_THICKNESS = 44;

const REPORT_LABELS: Record<CustomerReportType, string> = {
  'best-customers': 'Melhores Clientes',
  'purchase-frequency': 'Frequência de Compras',
  'average-ticket': 'Ticket Médio',
  'abc-curve': 'Curva ABC',
  'at-risk': 'Clientes em Risco',
  'sales-by-neighborhood': 'Vendas por Bairro',
};

const REPORT_DESCRIPTIONS: Record<CustomerReportType, string> = {
  'best-customers': 'Valor comprado pelos dez clientes com maior receita.',
  'purchase-frequency': 'Quantidade de clientes por perfil de frequência.',
  'average-ticket': 'Ticket médio dos dez clientes com maior valor por pedido.',
  'abc-curve': 'Valor comprado e participação por curva.',
  'at-risk': 'Clientes por nível de risco e curva ABC.',
  'sales-by-neighborhood': 'Valor vendido nos dez bairros com maior receita.',
};

function currency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function count(value: number): string {
  return value.toLocaleString('pt-BR');
}

function percentage(value: number): string {
  return `${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function chartTitle(reportType: CustomerReportType): string {
  return `${REPORT_LABELS[reportType]} — visualização`;
}

function isRanking(reportType: CustomerReportType): boolean {
  return reportType === 'best-customers'
    || reportType === 'average-ticket'
    || reportType === 'sales-by-neighborhood';
}

function visibleResponse(
  response: CustomerIntelligenceChartResponse,
): CustomerIntelligenceChartResponse {
  switch (response.reportType) {
    case 'best-customers':
      return { reportType: response.reportType, data: response.data.slice(0, RANKING_LIMIT) };
    case 'average-ticket':
      return { reportType: response.reportType, data: response.data.slice(0, RANKING_LIMIT) };
    case 'sales-by-neighborhood':
      return { reportType: response.reportType, data: response.data.slice(0, RANKING_LIMIT) };
    default:
      return response;
  }
}

function rowsForAlternative(
  response: CustomerIntelligenceChartResponse,
): string[] {
  switch (response.reportType) {
    case 'best-customers':
      return response.data.map((row: BestCustomersChartRow) =>
        `${row.customer}: ${currency(row.totalPurchased)} (${count(row.orderCount)} pedidos)`,
      );
    case 'purchase-frequency':
      return response.data.map((row: PurchaseFrequencyChartRow) =>
        `${row.frequencyProfile}: ${count(row.customerCount)} cliente(s)`,
      );
    case 'average-ticket':
      return response.data.map((row: AverageTicketChartRow) =>
        `${row.customer}: ${currency(row.averageTicket)} (${count(row.orderCount)} pedidos)`,
      );
    case 'abc-curve':
      return response.data.map((row: AbcCurveChartRow) =>
        `Curva ${row.curve}: ${currency(row.totalPurchased)}, ${count(row.customerCount)} cliente(s), ${percentage(row.participationPercentage)}`,
      );
    case 'at-risk':
      return response.data.map((row: AtRiskChartRow) =>
        `${row.risk}, curva ${row.curve}: ${count(row.customerCount)} cliente(s)`,
      );
    case 'sales-by-neighborhood':
      return response.data.map((row: SalesByNeighborhoodChartRow) =>
        `${row.neighborhood}: ${currency(row.totalSold)} (${count(row.orderCount)} pedidos)`,
      );
    default:
      return [];
  }
}

function contextMessage(
  reportType: CustomerReportType,
  visibleCount: number,
  totalResults?: number,
): string {
  if (isRanking(reportType)) {
    if (totalResults === undefined) return 'Exibindo até 10 resultados principais.';
    if (totalResults === 1) return 'Exibindo 1 de 1 resultado.';
    if (totalResults > visibleCount) {
      return `Exibindo os ${count(visibleCount)} principais de ${count(totalResults)} resultados. Consulte a lista abaixo para ver os demais.`;
    }
    return `Exibindo todos os ${count(totalResults)} resultados.`;
  }

  const categoryLabel = visibleCount === 1 ? 'categoria' : 'categorias';
  if (totalResults === undefined) {
    return `Exibindo resultados distribuídos entre ${visibleCount} ${categoryLabel}.`;
  }
  const customerLabel = totalResults === 1 ? 'cliente' : 'clientes';
  const distributedVerb = totalResults === 1 ? 'distribuído' : 'distribuídos';
  return `Exibindo ${count(totalResults)} ${customerLabel} ${distributedVerb} entre ${visibleCount} ${categoryLabel}.`;
}

const CustomerIntelligenceChart: React.FC<CustomerIntelligenceChartProps> = ({
  reportType,
  response,
  totalResults,
  loading,
  error,
  onRetry,
}) => {
  const currentResponse = useMemo(
    () => (response ? visibleResponse(response) : null),
    [response],
  );
  const alternativeRows = useMemo(
    () => (currentResponse ? rowsForAlternative(currentResponse) : []),
    [currentResponse],
  );
  const singleItem = currentResponse != null && currentResponse.data.length === 1;

  const chart = useMemo(() => {
    if (!currentResponse) return null;

    switch (currentResponse.reportType) {
      case 'best-customers': {
        const data: ChartData<'bar'> = {
          labels: currentResponse.data.map(row => row.customer),
          datasets: [{
            label: 'Valor comprado (R$)',
            data: currentResponse.data.map(row => row.totalPurchased),
            backgroundColor: '#329BFC',
            maxBarThickness: HORIZONTAL_MAX_BAR_THICKNESS,
          }],
        };
        const options: ChartOptions<'bar'> = {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<'bar'>) =>
                  `Valor comprado: ${currency(Number(context.raw))}`,
              },
            },
          },
        };
        return <Bar type="bar" data={data} options={options} />;
      }
      case 'purchase-frequency': {
        const data: ChartData<'bar'> = {
          labels: currentResponse.data.map(row => row.frequencyProfile),
          datasets: [{
            label: 'Clientes',
            data: currentResponse.data.map(row => row.customerCount),
            backgroundColor: '#329BFC',
            maxBarThickness: VERTICAL_MAX_BAR_THICKNESS,
          }],
        };
        const options: ChartOptions<'bar'> = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<'bar'>) =>
                  `Clientes: ${count(Number(context.raw))}`,
              },
            },
          },
        };
        return <Bar type="bar" data={data} options={options} />;
      }
      case 'average-ticket': {
        const data: ChartData<'bar'> = {
          labels: currentResponse.data.map(row => row.customer),
          datasets: [{
            label: 'Ticket médio (R$)',
            data: currentResponse.data.map(row => row.averageTicket),
            backgroundColor: '#329BFC',
            maxBarThickness: HORIZONTAL_MAX_BAR_THICKNESS,
          }],
        };
        const options: ChartOptions<'bar'> = {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<'bar'>) =>
                  `Ticket médio: ${currency(Number(context.raw))}`,
              },
            },
          },
        };
        return <Bar type="bar" data={data} options={options} />;
      }
      case 'abc-curve': {
        const data: ChartData<'doughnut'> = {
          labels: currentResponse.data.map(row => `Curva ${row.curve}`),
          datasets: [{
            label: 'Valor comprado (R$)',
            data: currentResponse.data.map(row => row.totalPurchased),
            backgroundColor: ['#329BFC', '#F9A825', '#D32F2F'],
          }],
        };
        const options: ChartOptions<'doughnut'> = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<'doughnut'>) =>
                  `Valor comprado: ${currency(Number(context.raw))}`,
              },
            },
          },
        };
        return <Doughnut type="doughnut" data={data} options={options} />;
      }
      case 'at-risk': {
        const labels = currentResponse.data.map(row => `${row.risk} — curva ${row.curve}`);
        const data: ChartData<'bar'> = {
          labels,
          datasets: [{
            label: 'Clientes',
            data: currentResponse.data.map(row => row.customerCount),
            backgroundColor: '#F9A825',
            maxBarThickness: VERTICAL_MAX_BAR_THICKNESS,
          }],
        };
        const options: ChartOptions<'bar'> = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<'bar'>) =>
                  `Clientes: ${count(Number(context.raw))}`,
              },
            },
          },
        };
        return <Bar type="bar" data={data} options={options} />;
      }
      case 'sales-by-neighborhood': {
        const data: ChartData<'bar'> = {
          labels: currentResponse.data.map(row => row.neighborhood),
          datasets: [{
            label: 'Valor vendido (R$)',
            data: currentResponse.data.map(row => row.totalSold),
            backgroundColor: '#329BFC',
            maxBarThickness: HORIZONTAL_MAX_BAR_THICKNESS,
          }],
        };
        const options: ChartOptions<'bar'> = {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<'bar'>) => {
                  const row = currentResponse.data[context.dataIndex];
                  return `Valor vendido: ${currency(Number(context.raw))} — ${count(row.orderCount)} pedidos`;
                },
              },
            },
          },
        };
        return <Bar type="bar" data={data} options={options} />;
      }
      default:
        return null;
    }
  }, [currentResponse]);

  const showLoading = loading || (!currentResponse && !error);
  const showData = !showLoading && !error && currentResponse && currentResponse.data.length > 0;

  return (
    <ChartSection aria-labelledby="customer-intelligence-chart-title">
      <h3 id="customer-intelligence-chart-title">{chartTitle(reportType)}</h3>
      <p>{REPORT_DESCRIPTIONS[reportType]}</p>
      <ChartViewport>
        {showLoading && <ChartStatus aria-live="polite">Carregando visualização...</ChartStatus>}
        {!showLoading && error && (
          <ChartStatus error role="alert">
            Não foi possível carregar a visualização.
            <br />
            <RetryChartButton type="button" onClick={onRetry}>
              Tentar novamente
            </RetryChartButton>
          </ChartStatus>
        )}
        {!showLoading && !error && currentResponse && currentResponse.data.length === 0 && (
          <ChartStatus aria-live="polite">Não há dados para visualizar no período selecionado.</ChartStatus>
        )}
        {showData && (
          <>
            {singleItem && (
              <ChartNotice role="status" aria-live="polite">
                Poucos dados para comparação. O gráfico representa o único resultado encontrado.
              </ChartNotice>
            )}
            <ChartPlot role="group" aria-label="Área de plotagem">
              <ChartCanvas dimmed={singleItem} aria-hidden="true">{chart}</ChartCanvas>
            </ChartPlot>
          </>
        )}
      </ChartViewport>
      {showData && currentResponse && (
        <ChartContext>
          {contextMessage(reportType, currentResponse.data.length, totalResults)}
        </ChartContext>
      )}
      {showData && (
        <TextAlternative>
          <summary>Ver dados da visualização em texto</summary>
          <ul>
            {alternativeRows.map(row => <li key={row}>{row}</li>)}
          </ul>
        </TextAlternative>
      )}
    </ChartSection>
  );
};

export default CustomerIntelligenceChart;
